"use client"

import { useEffect, useMemo, useState } from "react"
import API from "../utils/api"
import "./AdjustmentReport.css"
import { format } from "date-fns"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"

const AdjustmentReport = () => {
  const [activeTab, setActiveTab] = useState("today")
  const [fromDate, setFromDate] = useState(format(new Date(), "yyyy-MM-dd"))
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"))
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)

  useEffect(() => {
    if (activeTab === "today") {
      const today = format(new Date(), "yyyy-MM-dd")
      fetchByRange(today, today)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const groupAdjustments = (rows = []) => {
    const map = new Map()
    for (const r of rows) {
      const key = `${r.companyName || r.product?.companyName || "Unknown"}::${r.modelNo || r.product?.modelNo || "-"}::${r.size || r.product?.size || "-"}`
      if (!map.has(key)) {
        map.set(key, {
          key,
          companyName: r.companyName || r.product?.companyName || "Unknown",
          modelNo: r.modelNo || r.product?.modelNo || "-",
          size: r.size || r.product?.size || "-",
          incTotal: 0,
          decTotal: 0,
          net: 0,
          items: [],
        })
      }
      const g = map.get(key)
      const qty = Number(r.quantity) || 0
      if (String(r.action).toLowerCase() === "increase") {
        g.incTotal += qty
      } else if (String(r.action).toLowerCase() === "decrease") {
        g.decTotal += qty
      }
      g.net = g.incTotal - g.decTotal
      g.items.push(r)
    }
    return Array.from(map.values())
  }

  const fetchByRange = async (start, end) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ startDate: start, endDate: end })
      const res = await API.get(`/products/adjustments/report?${params.toString()}`)
      const rows = Array.isArray(res.data?.adjustments) ? res.data.adjustments : []
      setGroups(groupAdjustments(rows))
    } catch (err) {
      console.error("Error fetching adjustments:", err)
      setMessage("❌ Failed to load adjustment report")
      setGroups([])
    } finally {
      setLoading(false)
    }
  }

  const downloadPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
      doc.setFont("helvetica", "bold")
      doc.setFontSize(16)
      doc.text("Adjustment Report", 105, 15, { align: "center" })

      doc.setFont("helvetica", "normal")
      doc.setFontSize(10)
      doc.text(`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm")}`, 105, 22, { align: "center" })

      const body = (groups || []).map((g) => [
        `${g.companyName} - ${g.modelNo}`,
        g.size,
        String(g.incTotal),
        String(g.decTotal),
        String(g.net),
      ])

      autoTable(doc, {
        head: [["Product", "Size", "Increased", "Decreased", "Net"]],
        body,
        startY: 25,
        margin: { top: 25 },
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], halign: "center" },
        columnStyles: { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
      })

      doc.save(`adjustment-report-${format(new Date(), "yyyy-MM-dd")}.pdf`)
      setMessage("✅ PDF downloaded successfully")
    } catch (e) {
      setMessage("❌ Failed to generate PDF")
    }
  }

  const downloadCSV = () => {
    try {
      const header = ["Product", "Size", "Increased", "Decreased", "Net"]
      const rows = (groups || []).map((g) => [
        `${g.companyName} - ${g.modelNo}`,
        g.size,
        g.incTotal,
        g.decTotal,
        g.net,
      ])
      const csv = [header, ...rows]
        .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n")
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `adjustment-report-${format(new Date(), "yyyy-MM-dd")}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      setMessage("✅ CSV downloaded successfully")
    } catch (e) {
      setMessage("❌ Failed to generate CSV")
    }
  }

  return (
    <div className="adjustment-report-container">
      <div className="report-header">
        <h2>
          <i className="fas fa-clipboard-list"></i> Adjustment Report
        </h2>
        <div className="download-actions">
          <button onClick={downloadPDF} disabled={groups.length === 0} className="download-pdf-btn">
            <i className="fas fa-download"></i> Download PDF
          </button>
          <button onClick={downloadCSV} disabled={groups.length === 0} className="download-csv-btn">
            <i className="fas fa-file-excel"></i> Download Excel
          </button>
        </div>
      </div>

      <div className="report-controls">
        <div className="tabs">
          <button className={`tab-btn ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>Today</button>
          <button className={`tab-btn ${activeTab === 'range' ? 'active' : ''}`} onClick={() => setActiveTab('range')}>From–To</button>
        </div>
        <div className="range-controls">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <span className="separator">to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <button className="apply-btn" onClick={() => fetchByRange(fromDate, toDate)}>Apply</button>
        </div>
      </div>

      {loading ? (
        <p>Loading report...</p>
      ) : groups.length === 0 ? (
        <p>No adjustment records found.</p>
      ) : selectedGroup ? (
        <div className="report-details">
          <button className="back-btn" onClick={() => setSelectedGroup(null)}>← Back to Report</button>
          <h3>
            {selectedGroup.companyName} - {selectedGroup.modelNo} (Size: {selectedGroup.size})
          </h3>
          <table className="report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Action</th>
                <th>Quantity</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {selectedGroup.items.map((it, idx) => (
                <tr key={idx}>
                  <td>{it.date ? format(new Date(it.date), "yyyy-MM-dd") : '-'}</td>
                  <td>{it.adjustedBy || '-'}</td>
                  <td className={`action-cell ${String(it.action).toLowerCase()}`}>
                    {String(it.action).toLowerCase() === 'increase' ? 'Increase' : 'Decrease'}
                  </td>
                  <td>{it.quantity}</td>
                  <td>{it.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Totals</td>
                <td>+{selectedGroup.incTotal} / -{selectedGroup.decTotal}</td>
                <td>{selectedGroup.net}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <table className="report-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Size</th>
              <th>Increased</th>
              <th>Decreased</th>
              <th>Net</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key} className="clickable-row" onClick={() => setSelectedGroup(g)}>
                <td>{g.companyName} - {g.modelNo}</td>
                <td>{g.size}</td>
                <td className="inc">{g.incTotal}</td>
                <td className="dec">{g.decTotal}</td>
                <td>{g.net}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {message && <p className="report-message">{message}</p>}
    </div>
  )
}

export default AdjustmentReport

